import { MetricInfoModel } from '@logz-pkg/models';
import { ISearchRequestObject, ICollectionResponse } from '@logz-build/typescript';
import { chunk, range } from 'lodash';
import { ICrudApiService } from '../utilities/types';
import { CrudApiService } from '../utilities/crud.api.service';
import { metricsDropFiltersApiService } from '../metrics-drop-filters/api.service';
import { filterMetricsByDropFilters, getDropFilterPayload } from './populate';
import {
  IUtsSummary,
  UTS_SUMMARY_MAX_PAGES,
  UTS_SUMMARY_PAGE_CONCURRENCY,
  UTS_SUMMARY_PAGE_SIZE,
  summarizeUts,
} from './uts-summary';

export class MetricsInsightsService implements ICrudApiService<MetricInfoModel> {
  crudApiService = new CrudApiService({ modelDefinition: MetricInfoModel });

  urls = {
    search: '/data-optimization/metrics/usage/search',
  };

  search = async (requestObject: ISearchRequestObject): Promise<ICollectionResponse<MetricInfoModel>> => {
    const [metricsInsights, dropFilters] = await Promise.all([
      this.crudApiService.search(this.urls.search, requestObject),
      metricsDropFiltersApiService.search(getDropFilterPayload(requestObject)),
    ]);

    return filterMetricsByDropFilters(
      metricsInsights ?? { results: [], total: 0 },
      dropFilters ?? { results: [], total: 0 },
    );
  };

  /**
   * Total UTS for a filter, summed client-side because the service exposes no totals endpoint
   * (APPZ-3362) — `total` on the search response counts metrics, not time series.
   */
  getUtsSummary = async (
    filter: ISearchRequestObject['filter'] = {},
    abortSignal?: AbortSignal,
  ): Promise<IUtsSummary> => {
    const firstPage = await this.searchUtsPage({ filter, pageNumber: 1, abortSignal });
    const metricCount = firstPage.total;
    const pageCount = Math.min(Math.max(Math.ceil(metricCount / UTS_SUMMARY_PAGE_SIZE), 1), UTS_SUMMARY_MAX_PAGES);
    const metrics = [...firstPage.results];

    for (const batch of chunk(range(2, pageCount + 1), UTS_SUMMARY_PAGE_CONCURRENCY)) {
      // Sequential by construction: batches bound how many pages are in flight at once, and a
      // rejected batch stops the ones that would have followed.
      // eslint-disable-next-line no-await-in-loop
      const pages = await Promise.all(batch.map(pageNumber => this.searchUtsPage({ filter, pageNumber, abortSignal })));

      metrics.push(...pages.flatMap(page => page.results));
    }

    return summarizeUts({ metrics, metricCount });
  };

  private searchUtsPage = ({
    filter,
    pageNumber,
    abortSignal,
  }: {
    filter: ISearchRequestObject['filter'];
    pageNumber: number;
    abortSignal?: AbortSignal;
  }): Promise<ICollectionResponse<MetricInfoModel>> =>
    this.crudApiService.search(
      this.urls.search,
      {
        filter,
        // Biggest first, so a run that stops at the page ceiling still ranks the true top metrics
        // and sums the largest slice of the account it can reach.
        sort: [{ field: 'cardinality', descending: true }],
        pagination: { pageNumber, pageSize: UTS_SUMMARY_PAGE_SIZE },
      },
      { abortSignal },
    );
}

export const metricsInsightsService = new MetricsInsightsService();
